import { ref, readonly } from 'vue';
import { verifyQrIntercomLocation } from '../../api/qr/qrIntercom';

const LOCATION_TIMEOUT_MS = 10000;

const randomSixDigits = () => String(window.crypto.getRandomValues(new Uint32Array(1))[0] % 1000000).padStart(6, '0');

export function createQrVisitorContext(options = {}) {
    const locationStatus = ref('idle');
    const coordinates = ref(null);
    const verificationStatus = ref('idle');
    const verifiedEntranceId = ref(null);
    const verificationError = ref(null);
    let pending;

    // BE expects `visitor-` + 19 digits (like `date +%s%N`); ms timestamp + random digits keeps it unique
    function createCallSessionId() {
        const random = options.random || randomSixDigits;
        return `visitor-${Date.now()}${random()}`;
    }

    function requestLocation() {
        if (pending) return pending;
        coordinates.value = null;
        const geolocation = options.geolocation === undefined ? navigator.geolocation : options.geolocation;
        const secure = options.secure === undefined ? window.isSecureContext !== false : options.secure;
        if (!secure || !geolocation) {
            locationStatus.value = 'unsupported';
            return Promise.resolve(false);
        }
        locationStatus.value = 'pending';
        pending = new Promise(resolve => {
            let settled = false;
            const complete = (status, position) => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                locationStatus.value = status;
                coordinates.value = position || null;
                resolve(status === 'granted');
            };
            const timer = setTimeout(() => complete('timeout'), LOCATION_TIMEOUT_MS);
            try {
                geolocation.getCurrentPosition(position => {
                    const { latitude, longitude, accuracy } = position.coords;
                    if (![latitude, longitude, accuracy].every(Number.isFinite) ||
                        Math.abs(latitude) > 90 || Math.abs(longitude) > 180 || accuracy < 0) {
                        complete('unavailable');
                        return;
                    }
                    complete('granted', { latitude, longitude, accuracy });
                }, error => complete(({ 1: 'denied', 2: 'unavailable', 3: 'timeout' })[error.code] || 'unavailable'), {
                    enableHighAccuracy: true,
                    timeout: LOCATION_TIMEOUT_MS,
                    maximumAge: 0,
                });
            } catch {
                complete('unavailable');
            }
        }).finally(() => { pending = null; });
        return pending;
    }

    const ensureLocation = () => locationStatus.value === 'granted' ? Promise.resolve(true) : requestLocation();

    async function verifyLocation(entranceId) {
        verifiedEntranceId.value = null;
        verificationError.value = null;
        verificationStatus.value = 'pending';
        const located = await requestLocation();
        if (!located) {
            verificationStatus.value = 'idle';
            return false;
        }
        try {
            await verifyQrIntercomLocation(entranceId, coordinates.value);
            verifiedEntranceId.value = entranceId;
            verificationStatus.value = 'verified';
            return true;
        } catch (error) {
            verificationStatus.value = 'error';
            verificationError.value = error;
            return false;
        }
    }

    function isVerifiedFor(entranceId) {
        return verifiedEntranceId.value === entranceId && Boolean(coordinates.value);
    }

    function resetVerification() {
        verifiedEntranceId.value = null;
        verificationStatus.value = 'idle';
        verificationError.value = null;
    }

    function invalidateVerification(error) {
        verifiedEntranceId.value = null;
        verificationStatus.value = 'error';
        verificationError.value = error;
    }

    return {
        locationStatus: readonly(locationStatus),
        coordinates: readonly(coordinates),
        verificationStatus: readonly(verificationStatus),
        verificationError: readonly(verificationError),
        verifiedEntranceId: readonly(verifiedEntranceId),
        createCallSessionId,
        requestLocation,
        ensureLocation,
        verifyLocation,
        isVerifiedFor,
        invalidateVerification,
        resetVerification,
    };
}

const visitorContext = createQrVisitorContext();

export const useQrVisitorContext = () => visitorContext;